import React, { useContext, useMemo, useState, useCallback } from 'react';
import { EventFilter, NativePlatform } from '@fullstory/proto/fs/api/segment';
import {
  SignalStudioComponentProps,
  SignalStudioContext,
  usePreviewSearch,
} from '@fullstory/signal-studio-context';
import { useIntl } from 'react-intl';
import { SidebarElementPropertyRule } from '../components/SidebarElementPropertyRule';
import { useFlags } from '@fullstory/react-hooks';
import { Flag } from '@fullstory/proto/fs/shared/flags';
import {
  ElementPropertyRuleRecord,
  ElementPropertyRule,
} from '@fullstory/proto/fs/services/search-settings';
import {
  ElementPropertyRuleErrors,
  ElementPropertyRuleModalSource,
  ElementPropertyRuleModalVariant,
} from '../components';
import {
  getUnionDepsForSelectors,
  makeAvailableMultiRawValues,
  makeAvailableSingleRawValues,
} from '../utils';
import { useValidateElementPropertyRule } from '../components/ElementPropertyRuleModal/ElementPropertyRuleDetailsModal';
import { isEqual, noop } from 'lodash';
import { FsEvent } from '@fullstory/fs-script-api';
import { containsNonDescendantCombinator } from '@fullstory/selectors';
import { AvailableRawValues } from '../types';
import { useElementPropertyRulesWarnings } from './useElementPropertyRulesWarnings';
import { multiValueInfo } from '@fullstory/util/vars';
import { useElementPropertyRuleSelectors } from './useElementPropertyRuleSelectors';
import { useMatchesPrivacyRuleSelector } from './useMatchesPrivacyRuleSelector';
import { useElementPropertyRuleHighlights } from './useElementPropertyRuleHighlights';
import { useElementPropertyRuleConfig } from './useElementPropertyRuleConfig';
import { useNamedElements } from './useNamedElements';

const EMPTY_ERRORS: ElementPropertyRuleErrors = {};

export interface useElementPropertyRuleSignalStudioV2Props {
  elementPropertyRuleRecord: ElementPropertyRuleRecord;
  onCloseSignal: (saved?: boolean) => void;
  variant: ElementPropertyRuleModalVariant;
  onSave: (elementPropertyRule: ElementPropertyRule) => void;
  onArchive: (id: string) => void;
  onUnarchive: (id: string) => void;
  source: ElementPropertyRuleModalSource;
  sourceElementName?: string; // where we came from (for the back button), not necessarily the container element
}

export const useElementPropertyRuleSignalStudioV2 = (
  props: useElementPropertyRuleSignalStudioV2Props,
): {
  SignalStudio: React.ComponentType<SignalStudioComponentProps> | null;
  signalStudioProps: SignalStudioComponentProps;
} => {
  const intl = useIntl();
  const flags = useFlags();
  const canModifyProperties = flags.hasBit(Flag.PermModifyCustomVars);
  const SignalStudio = useContext(SignalStudioContext);
  const { namedElementMatcher } = useNamedElements();

  const { elementPropertyRule, setElementPropertyRule } = useElementPropertyRuleConfig(
    props.elementPropertyRuleRecord.elementPropertyRule!,
    true,
    true,
  );

  const definitionElementName = useMemo(() => {
    if (!elementPropertyRule.namedElementId) {
      return undefined;
    }
    return namedElementMatcher.matchById(elementPropertyRule.namedElementId)?.namedElement?.name;
  }, [elementPropertyRule.namedElementId, namedElementMatcher]);

  const { isMulti } = multiValueInfo(elementPropertyRule.propertyType);

  const [trimTrigger, setTrimTrigger] = useState<boolean>(false);

  const [activeIndex, setActiveIndex] = useState(0);
  const [previewState, setPreviewState] = useState(0);
  const incrementPreviewState = useCallback(() => setPreviewState(current => current + 1), []);

  const eventFiltersPreview = useMemo(
    (): (EventFilter | null)[] => [
      // prefer click
      { click: { any: {} } },
      // but fall back to any activity if no click data is available
      null,
    ],
    [],
  );

  const {
    selectors,
    childSelectors,
    hasNamedElementSelectors,
    hasChild,
    hasChildAndNonEmptyParent,
  } = useElementPropertyRuleSelectors(elementPropertyRule);

  const isPrivacyRuleSelector = useMatchesPrivacyRuleSelector(
    hasChild ? childSelectors : selectors,
  );

  const fallbackDeps = useMemo(
    () => getUnionDepsForSelectors(selectors),
    [hasNamedElementSelectors, selectors],
  );

  const previewSearches = usePreviewSearch(
    eventFiltersPreview,
    [fallbackDeps],
    incrementPreviewState,
  );
  const previewActiveIndex = 0; // for preview purposes, always use the sole preview search
  const visualUnsupported = !!elementPropertyRule?.any || !!elementPropertyRule.anyChild;

  const onDeleteIndex = useCallback(
    (index: number) => {
      if ((index === activeIndex && activeIndex > 0) || index < activeIndex) {
        setActiveIndex(activeIndex - 1);
      }
    },
    [activeIndex, setActiveIndex],
  );

  const onInspectSelector = useCallback(
    (
      selector: string,
      canonicalizedSelector: string,
      nativePlatform: NativePlatform | undefined,
    ) => {
      // inspection always updates the child selector
      // the parent is always a named element containing the configuration
      // unset the other options before setting the selectors
      const { anyChild, childNamedElementId, ...updatedElementPropertyRule } = elementPropertyRule;
      setElementPropertyRule({
        ...updatedElementPropertyRule,
        childSelectors: {
          selectors: [
            ...(updatedElementPropertyRule.childSelectors?.selectors?.slice(0, activeIndex) ?? []),
            {
              selector,
              originalSelector: selector,
              canonicalizedSelector,
              platforms: nativePlatform ? [nativePlatform] : [],
            },
            ...(updatedElementPropertyRule.childSelectors?.selectors?.slice(activeIndex + 1) ?? []),
          ],
        },
      });
      // And finally, when applicable, auto trim after inspection.
      const shouldAutoTrim = !containsNonDescendantCombinator(selector);
      setTrimTrigger(shouldAutoTrim);
    },
    [activeIndex, hasChild, elementPropertyRule, setElementPropertyRule],
  );

  const {
    highlightedObjects,
    valueSourceObjects,
    previewElements,
    attachedElements,
    highlights,
    highlightConstraints,
    onHighlightedObjects,
  } = useElementPropertyRuleHighlights({
    selectors,
    childSelectors,
    hasChild,
    hasChildAndNonEmptyParent,
    isMulti,
    constrainInspected: true,
  });

  const availableValues = useMemo((): AvailableRawValues | undefined => {
    if (visualUnsupported) {
      return undefined;
    }
    return isMulti
      ? makeAvailableMultiRawValues(valueSourceObjects)
      : makeAvailableSingleRawValues(valueSourceObjects);
  }, [visualUnsupported, valueSourceObjects, isMulti]);

  const validate = useValidateElementPropertyRule();
  const { errors, valid } = useMemo(() => {
    const errors = validate(elementPropertyRule);
    return {
      valid: isEqual(errors, {}),
      errors,
    };
  }, [elementPropertyRule, validate]);
  const [showErrors, setShowErrors] = useState(false);

  const warnings = useElementPropertyRulesWarnings({
    elementPropertyRule,
    availableValues,
    highlightedObjects,
    isPrivacyRuleSelector,
  });

  const ConfigSidebar = (
    <SidebarElementPropertyRule
      elementPropertyRule={elementPropertyRule}
      archived={props.elementPropertyRuleRecord?.metadata?.archived || false}
      onChange={setElementPropertyRule}
      availableValues={availableValues}
      variant={props.variant}
      errors={showErrors ? errors : EMPTY_ERRORS}
      visibleElements={attachedElements}
      omitNonVisibleElements // v2 only shows elements with the containing element
      attachedElements={attachedElements}
      activeIndex={{
        index: activeIndex,
        setIndex: setActiveIndex,
        deleteIndex: onDeleteIndex,
      }}
      trimTrigger={trimTrigger}
      setTrimTrigger={setTrimTrigger}
      warnings={warnings}
      definitionElementName={definitionElementName}
      sidebarV2Enabled
    />
  );

  const archived = props.elementPropertyRuleRecord?.metadata?.archived || false;
  const metadata = useMemo(() => {
    if (
      !props.elementPropertyRuleRecord?.metadata ||
      props.variant === ElementPropertyRuleModalVariant.new
    ) {
      return undefined;
    }
    return {
      createdBy: props.elementPropertyRuleRecord.metadata.createdBy,
      updatedBy: props.elementPropertyRuleRecord.metadata.lastUpdatedBy,
      createdAt: props.elementPropertyRuleRecord.metadata.created,
      updatedAt: props.elementPropertyRuleRecord.metadata.lastUpdated,
    };
  }, [props.elementPropertyRuleRecord?.metadata, props.variant]);

  return {
    SignalStudio,
    signalStudioProps: {
      header: elementPropertyRule?.name ?? '',
      backMessage: props.sourceElementName
        ? intl.formatMessage(
            { defaultMessage: 'Back to Element: {sourceElementName}', id: 'H/UElo' },
            { sourceElementName: props.sourceElementName },
          )
        : undefined,
      lowerConfigPartName: intl.formatMessage({ defaultMessage: 'selector', id: 'A7ml5w' }),
      configSidebar: ConfigSidebar,
      preview: {
        elements: previewElements,
        state: previewState,
        index: previewActiveIndex,
        onIndexChange: noop, // nothing to do here, there is only ever one preview given sole named element parent
        mode: 'element-inspector',
        previews: previewSearches.map(search => ({
          search,
          visualUnsupportedMessage: visualUnsupported
            ? intl.formatMessage({
                defaultMessage: "Previews are not supported for 'any' rules.",
                id: 'Lr7jUa',
              })
            : undefined,
        })),
        highlights,
        highlightConstraints,
        onHighlightedObjects,
        onInspectSelector,
      },
      onConfirm: () => {
        setShowErrors(!valid);
        if (valid) {
          FsEvent.recordCustomEventV2('Property Extraction Rule Saved', {
            is_new: props.variant === ElementPropertyRuleModalVariant.new,
            source: ElementPropertyRuleModalSource[props.source],
            id: elementPropertyRule?.id ?? '',
          });
          props.onSave(elementPropertyRule);
          props.onCloseSignal(true);
        }
      },
      onCloseSignal: () => {
        props.onCloseSignal(false);
      },
      onArchive: () => {
        if (props.variant === ElementPropertyRuleModalVariant.new || !elementPropertyRule.id) {
          return;
        }
        const customEventName = 'Property Extraction Rule Archived';
        FsEvent.recordCustomEventV2(customEventName, {
          id: elementPropertyRule.id,
          archived: true,
        });
        props.onArchive(elementPropertyRule.id);
        props.onCloseSignal(false);
      },
      onUnarchive: () => {
        const customEventName = 'Property Extraction Rule Archived';
        FsEvent.recordCustomEventV2(customEventName, {
          id: elementPropertyRule.id,
          archived: false,
        });
        props.onUnarchive(elementPropertyRule.id);
        props.onCloseSignal(false);
      },
      archived,
      disableEdit: archived || !canModifyProperties,
      disableArchive:
        !canModifyProperties || !archived
          ? props.variant === ElementPropertyRuleModalVariant.new
          : !!errors.name,
      emptyMessage: intl.formatMessage(
        {
          defaultMessage:
            'Property Rule Preview<b></b>Load preview to start defining your property rule.',
          id: '1KAogp',
        },
        {
          b: () => <br />,
        },
      ),
      metadata,
    },
  };
};
